import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../../context/AuthContext';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import AdminSectionLayout from '../../components/AdminSectionLayout';


export default function GalleryManagement() {
    const { id } = useParams();
    const { user } = useAuthContext();
    const [galleryData, setGalleryData] = useState({ gallery: {}, users: [] });
    const [artworks, setArtworks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
  
    const fetchGallery = useCallback(async () => {
        try {
          const response = await axiosInstance.get(`/gallery/${id}`);
          setGalleryData(response.data);
        } catch (error) {
          console.error('Error fetching gallery:', error);
        }
      }, [id]);

    const fetchArtworks = useCallback(async () => {
        try {
          if (!user?.token) {
            console.log('No user token available');
            return;
          }

          // Use the actual galleryId from the gallery data, not the MongoDB ObjectId from URL
          const actualGalleryId = galleryData.gallery?.galleryId;
          
          if (!actualGalleryId) {
            console.log('Gallery ID not yet loaded');
            return;
          }

          console.log('Gallery data:', galleryData);
          console.log('Actual Gallery ID:', actualGalleryId);
          
          // Fetch artworks specifically for this gallery ID using the simple route
          const url = `/artworks/artworks?galleryId=${actualGalleryId}`;
          console.log('Fetching artworks from URL:', url);
          
          const response = await axiosInstance.get(url);
          const json = response.data;
          console.log('Artworks response for gallery', actualGalleryId, ':', json);
          
          if (json.artworks) {
            setArtworks(json.artworks);
          } else if (Array.isArray(json)) {
            setArtworks(json);
          } else {
            console.log('Unexpected response structure:', json);
            setArtworks([]);
          }
        } catch (error) {
          console.error('Error fetching artworks:', error);
        }
      }, [user?.token, galleryData.gallery?.galleryId]);
    
  
      const fetchUsersByName = useCallback(async () => {
        try {
            if (searchTerm.trim() !== '') {
                const response = await axiosInstance.get(
                    `/user?limit=${100}&page=${1}&search=${searchTerm}`,
                );
                setSearchResults(response.data);
            } else {
                // If the search term is empty, set searchResults to an empty array
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }, [searchTerm]);

    useEffect(() => {
        if(!searchTerm){
            setSearchResults([]);
            
          }
        fetchUsersByName();
    }, [fetchUsersByName, searchTerm]);
    
    useEffect(() => {
      fetchGallery();
    }, [fetchGallery]);

    useEffect(() => {
      // Fetch artworks after gallery data is loaded
      if (galleryData.gallery?.galleryId) {
        fetchArtworks();
      }
    }, [fetchArtworks, galleryData.gallery?.galleryId]);
  
    
  
    const handleUserClick = async (userId) => {

      try {
        await axiosInstance.put(`/user/${userId}`, {
          galleryId: id,
          method: 'add'
        });
        setSearchTerm('')
        fetchGallery();
        setSearchResults([]);
      } catch (error) {
        console.error('Error updating user:', error);
      }
    };

    const handleRemoveUserClick = async (userId) => {
        try {
            await axiosInstance.put(`/user/${userId}`, {
                galleryId: id,
                method: 'delete'
              });
        
      
          fetchGallery();
          setSearchResults([]);
        } catch (error) {
          console.error('Error removing user:', error);
        }
      };

    const handleDeleteArtwork = async (artworkId) => {
        try {
          await axiosInstance.delete(`/artworks/${artworkId}`);
          fetchArtworks(); // Refresh the artworks list
        } catch (error) {
          console.error('Error deleting artwork:', error);
        }
      };

    const handleViewArtwork = (artworkId) => {
        window.open(`/artwork/${artworkId}`, '_blank');
      };
  
    // Render connected users in a table
    const renderConnectedUsers = () => (
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="min-w-full table-fixed">
              <thead className="sticky top-0 bg-white border-b border-gray-100">
                <tr>
                  <th className="pl-4 py-2 text-left">User</th>
                  <th className="pl-4 py-2 text-left">Email</th>
                  <th className="pl-4 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody className="text-xs sm:text-sm md:text-base">
                {galleryData.users.map((user) => (
                  <tr key={user._id}>
                    <td className="px-4 py-3 border-t">
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="px-4 py-3 border-t border-l">{user.email}</td>
                    <td className="px-4 py-2 border-t border-l">
                      <button
                        className="px-2 py-1 font-semibold text-white bg-red-500 border border-red-500 rounded hover:bg-red-600 hover:text-white hover:border-transparent"
                        onClick={() => handleRemoveUserClick(user._id)}
                      >
                        Remove user
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );

    // Render connected artworks in a table
    const renderConnectedArtworks = () => (
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="min-w-full table-fixed">
              <thead className="sticky top-0 bg-white border-b border-gray-100">
                <tr>
                  <th className="pl-4 py-2 text-left">Artwork Title</th>
                  <th className="pl-4 py-2 text-left">Artist Email</th>
                  <th className="pl-4 py-2 text-left">Camera ID</th>
                  <th className="pl-4 py-2 text-left">Created</th>
                  <th className="pl-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="text-xs sm:text-sm md:text-base">
                {artworks.map((artwork) => (
                  <tr key={artwork._id}>
                    <td className="px-4 py-3 border-t">
                      <div className="flex items-center">
                        <img 
                          src={artwork.imageBase64} 
                          alt={artwork.title}
                          className="w-12 h-12 object-cover rounded mr-3"
                        />
                        <span className="font-medium">{artwork.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 border-t border-l">{artwork.artist_email}</td>
                    <td className="px-4 py-3 border-t border-l">{artwork.RaspID}</td>
                    <td className="px-4 py-3 border-t border-l">
                      {new Date(artwork.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 border-t border-l">
                      <div className="flex gap-2">
                        <button
                          className="px-2 py-1 font-semibold text-white bg-blue-500 border border-blue-500 rounded hover:bg-blue-600 hover:text-white hover:border-transparent"
                          onClick={() => handleViewArtwork(artwork._id)}
                        >
                          View
                        </button>
                        <button
                          className="px-2 py-1 font-semibold text-white bg-red-500 border border-red-500 rounded hover:bg-red-600 hover:text-white hover:border-transparent"
                          onClick={() => handleDeleteArtwork(artwork._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
    
  
    return (
      <>
        <Navbar />
        <AdminSectionLayout
          title={galleryData.gallery?.displayName || 'Gallery Management'}
          description="Manage gallery information and connected users."
        >
          {galleryData ? (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Gallery Details</h2>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><span className="font-medium text-gray-900">Gallery ID:</span> {galleryData.gallery?.galleryId}</p>
                  <p><span className="font-medium text-gray-900">Technical Contact Email:</span> {galleryData.gallery?.technicalContactEmail}</p>
                  <p><span className="font-medium text-gray-900">Technical Contact Phone:</span> {galleryData.gallery?.technicalContactPhone}</p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Connected Users</h2>

                <div className="w-full sm:w-96 mb-4">
                  <input
                    type="text"
                    placeholder="Search users by name"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />

                  {searchResults.users?.length > 0 && (
                    <ul className="mt-2 p-2 list-none bg-white border border-gray-200 rounded-lg shadow-sm">
                      {searchResults.users.map((user) => (
                        <li
                          key={user._id}
                          onClick={() => handleUserClick(user._id)}
                          className="mb-2 p-2 border-b border-gray-100 cursor-pointer hover:bg-gray-50 last:border-b-0"
                        >
                          <strong className="text-sm text-gray-900">
                            {user.firstName} {user.lastName}
                          </strong>
                          <br />
                          <small className="text-xs text-gray-600">{user.email}</small>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {renderConnectedUsers()}
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Gallery Artworks</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Total artworks: {artworks.length}
                </p>
                {artworks.length > 0 ? (
                  renderConnectedArtworks()
                ) : (
                  <div className="w-full p-8 text-center bg-white rounded-xl border border-gray-100 shadow-sm">
                    <p className="text-gray-500">No artworks found for this gallery.</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Gallery ID: {galleryData.gallery?.galleryId || 'Loading...'}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-gray-600">Loading gallery information...</p>
          )}
        </AdminSectionLayout>
      </>
    );
  }
